import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileSidebar } from './MobileSidebar';
import { TrialBanner } from '@/shared/components/TrialBanner/TrialBanner';
import { DeliverySetupBanner } from '@/shared/components/DeliverySetupBanner/DeliverySetupBanner';
import { VaptAiLauncher } from '@/features/vapt-ai/components/VaptAiLauncher';
import { PrintingStatusBar } from '@/features/printing/components/PrintingStatusBar';
import { AgentUpdatePrompt } from '@/features/printing/components/AgentUpdatePrompt';
import { AiUnitsBanner } from '@/features/billing/components/AiUnitsBanner';
import { WhatsappStatusBar } from '@/features/whatsapp/components/WhatsappStatusBar';
import { ConversationAlerts } from '@/features/conversations/components/ConversationAlerts';
import { CustomerAgentStatusBar } from '@/features/conversations/components/CustomerAgentStatusBar';
import styles from './PanelLayout.module.css';

export function PanelLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const bannersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOutsideMobile = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener('resize', closeOutsideMobile);
    return () => window.removeEventListener('resize', closeOutsideMobile);
  }, []);

  // Publica a altura dos banners para o CSS montar `--panel-sticky-top`. Os
  // banners aparecem depois das próprias consultas e mudam de altura quando o
  // texto quebra, então só medindo dá para saber onde começa a área livre.
  // Vai direto no estilo, sem estado: medir não deve renderizar a página.
  useEffect(() => {
    const layout = layoutRef.current;
    const banners = bannersRef.current;
    if (!layout || !banners || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      layout.style.setProperty('--panel-banners-height', `${banners.offsetHeight}px`);
    });
    observer.observe(banners);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={layoutRef} className={styles.layout}>
      <div className={styles.sidebar}>
        <Sidebar />
      </div>

      <MobileSidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        returnFocusRef={menuButtonRef}
      />

      <div className={styles.main}>
        <Topbar
          onMenuToggle={() => setMobileOpen((v) => !v)}
          menuButtonRef={menuButtonRef}
        />
        {/*
          Os banners grudam como um bloco só. Sticky individual, cada um com o
          mesmo `top: 64px`, faria o segundo cobrir o primeiro na rolagem — e o
          caso comum é os dois juntos, porque um tenant recém-cadastrado está
          em trial E sem configuração de entrega.
        */}
        <div ref={bannersRef} className={styles.banners}>
          {/* A impressão primeiro: é a única faixa que diz que o papel não
              está saindo na cozinha agora. Ela mesma decide se aparece. */}
          <PrintingStatusBar />
          {/* Clientes esperando a equipe no WhatsApp (change
              customer-agent-handoff, D19): logo depois da impressão, com o
              som e o título da aba. Um só no painel: fonte nova de alerta
              entra em `lib/alert-sources.ts`. Ele mesmo decide se aparece
              (flag, direito do plano e `conversations.read`). */}
          <ConversationAlerts />
          {/* O Atendente IA parado por algo que a loja não escolheu (Vapt,
              provedor ou teto do mês, D20). Ele mesmo decide se aparece. */}
          <CustomerAgentStatusBar />
          {/* A conexão do WhatsApp do Atendente IA: caiu, a Meta pede uma
              ação, as mensagens não saem ou o lembrete dos 14 dias. Ela mesma
              decide se aparece (flag, direito do plano e `settings.read`). */}
          <WhatsappStatusBar />
          {/* Os atendimentos do Atendente IA (change billing-ai-units): ela
              mesma decide se aparece (plano, permissão e aviso ativo). */}
          <AiUnitsBanner />
          <TrialBanner />
          <DeliverySetupBanner />
        </div>
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>

      {/*
        Fora do `main` porque a gaveta acompanha o painel inteiro, não a página
        aberta. Ele mesmo decide se aparece: sem a flag ou sem permissão de
        cardápio, não renderiza nada.
      */}
      <VaptAiLauncher />

      {/*
        Aviso e diálogo de atualização do computador da impressão, por cima de
        qualquer tela. Ele mesmo decide se aparece: só para quem pode pedir a
        atualização e com a impressão disponível na loja.
      */}
      <AgentUpdatePrompt />
    </div>
  );
}
